import { useEffect, useState, type FormEvent } from 'react'
import { Icon, type IconName, Screen, TopNav, useMobile, useRoleFlags } from './shared'
import PrinterSettings from './PrinterSettings'
import { LANGS, setLang, useT, type MsgKey } from './i18n'

/* App settings for the phone app: appearance, which sources the notification
 * feed is allowed to show, and a PIN lock for a device the desk shares.
 *
 * All three are preferences of this device rather than clinic records, so they
 * live in localStorage and never reach the API. Two people signed in to the
 * same account on two tablets are meant to be able to differ here.
 *
 * Every touch of storage is wrapped: localStorage throws outright in a private
 * window and in some embedded webviews, and a preferences screen must never be
 * the thing that breaks the app. */

/* ---------- storage keys, shared with the lock screen ---------- */

export const THEME_KEY = 'clinic.m.theme'
export const NOTIFY_KEY = 'clinic.m.notifyPrefs'
export const LOCK_PIN_KEY = 'clinic.m.lockPin'
export const LOCK_ON_KEY = 'clinic.m.lockOn'
export const UNLOCKED_KEY = 'clinic.m.unlockedAt'

/** Guarded reads and writes. A missing value and an unavailable store are the
 *  same thing to every caller here: fall back to the default and carry on. */
export function readStore(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function writeStore(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* The preference is a convenience. Losing it must not stop the tap. */
  }
}

export function removeStore(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* Same reasoning as writeStore. */
  }
}

/* ---------- appearance ---------- */

export type ThemePref = 'light' | 'dark' | 'system'

const THEMES: { value: ThemePref; label: MsgKey }[] = [
  { value: 'light', label: 'account.sThemeLight' },
  { value: 'dark', label: 'account.sThemeDark' },
  { value: 'system', label: 'account.sThemeSystem' },
]

export function readTheme(): ThemePref {
  const raw = readStore(THEME_KEY)
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system'
}

/** The dark palette is a token override on `.mapp[data-theme="dark"]`, so the
 *  whole of appearance is one attribute on one element. Light is the absence of
 *  the attribute rather than a value of its own, which keeps the default
 *  palette exactly where it was. */
function stamp(dark: boolean): void {
  // Re-queried on every call: the router swaps screens under the frame, and a
  // node captured once can be stale by the time the media query fires.
  const el = document.querySelector('.mapp')
  if (!el) return
  if (dark) el.setAttribute('data-theme', 'dark')
  else el.removeAttribute('data-theme')
}

/**
 * Apply a preference and return the teardown for whatever it subscribed to.
 * Only the subscription is torn down, not the attribute: the palette should
 * survive navigating away from this screen.
 */
export function applyTheme(pref: ThemePref): () => void {
  if (pref !== 'system') {
    stamp(pref === 'dark')
    return () => { /* Nothing was subscribed. */ }
  }

  // Older webviews ship without matchMedia. Following the device is then
  // impossible, so fall back to light rather than throwing on a settings tap.
  if (typeof window.matchMedia !== 'function') {
    stamp(false)
    return () => { /* Nothing was subscribed. */ }
  }

  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const sync = () => stamp(mq.matches)
  sync()
  mq.addEventListener('change', sync)
  return () => mq.removeEventListener('change', sync)
}

/* ---------- notification preferences ---------- */

export interface NotifyPrefs {
  queue: boolean
  bookings: boolean
  results: boolean
  reminders: boolean
}

const NOTIFY_DEFAULTS: NotifyPrefs = { queue: true, bookings: true, results: true, reminders: true }

/** Anything unparseable falls back to "show everything", because a corrupt
 *  preference blob must not silently hide a patient who is still waiting. */
export function readNotifyPrefs(): NotifyPrefs {
  const raw = readStore(NOTIFY_KEY)
  if (!raw) return { ...NOTIFY_DEFAULTS }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return { ...NOTIFY_DEFAULTS }
    const rec = parsed as Record<string, unknown>
    const pick = (k: keyof NotifyPrefs): boolean => (typeof rec[k] === 'boolean' ? rec[k] : true)
    return { queue: pick('queue'), bookings: pick('bookings'), results: pick('results'), reminders: pick('reminders') }
  } catch {
    return { ...NOTIFY_DEFAULTS }
  }
}

const NOTIFY_ROWS: { key: keyof NotifyPrefs; icon: IconName; label: MsgKey; sub: MsgKey }[] = [
  { key: 'queue', icon: 'clock', label: 'account.sQueue', sub: 'account.sQueueSub' },
  { key: 'bookings', icon: 'calendar', label: 'account.sBookings', sub: 'account.sBookingsSub' },
  { key: 'results', icon: 'flask', label: 'account.sResults', sub: 'account.sResultsSub' },
  { key: 'reminders', icon: 'send', label: 'account.sReminders', sub: 'account.sRemindersSub' },
]

/* ---------- app lock ---------- */

export function isPin(value: string): boolean {
  return /^\d{4}$/.test(value)
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, 4)
}

type LockMode = 'idle' | 'set' | 'change' | 'off'

/* ---------- screen ---------- */

export default function AppSettings() {
  const { toast } = useMobile()
  /** The desk prints token slips; a patient has no printer to set up. */
  const { isPatient } = useRoleFlags()

  const [theme, setTheme] = useState<ThemePref>(() => readTheme())
  const { lang, t } = useT()
  const [notify, setNotify] = useState<NotifyPrefs>(() => readNotifyPrefs())

  const [lockOn, setLockOn] = useState(() => readStore(LOCK_ON_KEY) === '1')
  const [hasPin, setHasPin] = useState(() => isPin(readStore(LOCK_PIN_KEY) ?? ''))
  const [mode, setMode] = useState<LockMode>('idle')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)

  useEffect(() => applyTheme(theme), [theme])

  function chooseTheme(value: ThemePref) {
    setTheme(value)
    writeStore(THEME_KEY, value)
  }

  function toggleNotify(key: keyof NotifyPrefs) {
    setNotify((prev) => {
      const updated = { ...prev, [key]: !prev[key] }
      writeStore(NOTIFY_KEY, JSON.stringify(updated))
      return updated
    })
  }

  function openMode(nextMode: LockMode) {
    setMode(nextMode)
    setCurrent('')
    setNext('')
    setAgain('')
    setPinError(null)
  }

  function submitPin(e: FormEvent) {
    e.preventDefault()
    const stored = readStore(LOCK_PIN_KEY) ?? ''

    // Changing or clearing the PIN asks for the old one first, so a tablet left
    // unlocked for a minute cannot be quietly re-keyed by whoever walks past.
    if ((mode === 'change' || mode === 'off') && current !== stored) {
      setPinError(t('account.sPinWrong'))
      return
    }

    if (mode === 'off') {
      removeStore(LOCK_PIN_KEY)
      removeStore(UNLOCKED_KEY)
      writeStore(LOCK_ON_KEY, '0')
      setHasPin(false)
      setLockOn(false)
      openMode('idle')
      toast({ kind: 'ok', text: t('account.sLockTurnedOff') })
      return
    }

    if (!isPin(next)) {
      setPinError(t('account.sPinFour'))
      return
    }
    if (next !== again) {
      setPinError(t('account.sPinMismatch'))
      return
    }

    writeStore(LOCK_PIN_KEY, next)
    writeStore(LOCK_ON_KEY, '1')
    // Stamping the unlock now means setting a PIN does not immediately throw
    // the person who set it onto the lock screen.
    writeStore(UNLOCKED_KEY, String(Date.now()))
    setHasPin(true)
    setLockOn(true)
    openMode('idle')
    toast({ kind: 'ok', text: mode === 'change' ? t('account.sPinChanged') : t('account.sLockIsOn') })
  }

  /** The switch turns the lock on without ceremony, but turning it off is a
   *  security step down, so it goes through the PIN form instead. */
  function toggleLock() {
    if (!hasPin) {
      openMode('set')
      return
    }
    if (lockOn) {
      openMode('off')
      return
    }
    writeStore(LOCK_ON_KEY, '1')
    writeStore(UNLOCKED_KEY, String(Date.now()))
    setLockOn(true)
    toast({ kind: 'ok', text: t('account.sLockIsOn') })
  }

  return (
    <Screen>
      <TopNav title={t('account.appSettings')} />

      <div className="m-settings">

      <div className="m-section">
        <div className="m-section-head"><h2>{t('account.sAppearance')}</h2></div>
        <p className="m-note">
          {t('account.sAppearanceNote')}
        </p>
        <div className="m-seg" role="group" aria-label={t('account.sAppearance')}>
          {THEMES.map((th) => (
            <button
              key={th.value}
              type="button"
              className={theme === th.value ? 'is-on' : ''}
              aria-pressed={theme === th.value}
              onClick={() => chooseTheme(th.value)}
            >
              {t(th.label)}
            </button>
          ))}
        </div>
        <div className="m-card m-menu">
          <div className="m-menu-item">
            <span className="ico"><Icon name="moon" size={18} /></span>
            <span className="lbl">
              {t('account.sPhoneOnly')}
              <span className="m-note" style={{ display: 'block', fontWeight: 500 }}>
                {t('account.sDesktopPalette')}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="m-section">
        <div className="m-section-head"><h2>Language · भाषा</h2></div>
        <p className="m-note">
          {t('account.sLanguageNote')}
        </p>
        <div className="m-seg" role="group" aria-label={t('account.sLanguage')}>
          {LANGS.map((l) => (
            <button
              key={l.code} type="button" lang={l.code}
              className={lang === l.code ? 'is-on' : ''}
              aria-pressed={lang === l.code}
              onClick={() => setLang(l.code)}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="m-section">
        <div className="m-section-head"><h2>{t('account.nTitle')}</h2></div>
        <p className="m-note">
          {t('account.sNotifyNote')}
        </p>
        <div className="m-card m-menu">
          {NOTIFY_ROWS.map((row) => (
            <label key={row.key} className="m-menu-item" htmlFor={`n-${row.key}`}>
              <span className="ico"><Icon name={row.icon} size={18} /></span>
              <span className="lbl">
                {t(row.label)}
                <span className="m-note" style={{ display: 'block', fontWeight: 500 }}>{t(row.sub)}</span>
              </span>
              <input
                id={`n-${row.key}`}
                className="m-switch"
                type="checkbox"
                checked={notify[row.key]}
                onChange={() => toggleNotify(row.key)}
              />
            </label>
          ))}
        </div>
        {!notify.queue && (
          <p className="m-note warn">
            {t('account.sQueueHidden')}
          </p>
        )}
      </div>

      {!isPatient && <PrinterSettings />}

      <div className="m-section">
        <div className="m-section-head">
          <h2>{t('account.sAppLock')}</h2>
          <span className="m-link">{lockOn ? t('account.sOn') : t('account.sOff')}</span>
        </div>
        <p className="m-note">
          {t('account.sLockNote')}
        </p>

        <div className="m-card m-menu">
          <label className="m-menu-item" htmlFor="lock-on">
            <span className="ico"><Icon name="shield" size={18} /></span>
            <span className="lbl">
              {t('account.sLockWithPin')}
              <span className="m-note" style={{ display: 'block', fontWeight: 500 }}>
                {hasPin ? t('account.sPinSet') : t('account.sNoPin')}
              </span>
            </span>
            <input
              id="lock-on"
              className="m-switch"
              type="checkbox"
              checked={lockOn}
              onChange={toggleLock}
            />
          </label>

          {hasPin && mode === 'idle' && (
            <button type="button" className="m-menu-item" onClick={() => openMode('change')}>
              <span className="ico"><Icon name="key" size={18} /></span>
              <span className="lbl">{t('account.sChangePin')}</span>
              <Icon name="chevron" size={16} />
            </button>
          )}
        </div>

      {mode !== 'idle' && (
          <form className="m-card m-form" onSubmit={submitPin}>
            {pinError && (
              <div className="m-alert-banner wide"><Icon name="alert" size={18} />{pinError}</div>
            )}

            {(mode === 'change' || mode === 'off') && (
              <div className="m-field wide">
                <label htmlFor="pin-cur">{t('account.sCurrentPin')}</label>
                <input
                  id="pin-cur" type="password" inputMode="numeric" autoComplete="off"
                  maxLength={4} value={current}
                  onChange={(e) => { setCurrent(digitsOnly(e.target.value)); setPinError(null) }}
                />
              </div>
            )}

            {mode !== 'off' && (
              <>
                <div className="m-field">
                  <label htmlFor="pin-new">{mode === 'change' ? t('account.sNewPin') : t('account.sPin')}</label>
                  <input
                    id="pin-new" type="password" inputMode="numeric" autoComplete="off"
                    maxLength={4} value={next}
                    onChange={(e) => { setNext(digitsOnly(e.target.value)); setPinError(null) }}
                  />
                </div>
                <div className="m-field">
                  <label htmlFor="pin-again">{t('account.sRepeat')}</label>
                  <input
                    id="pin-again" type="password" inputMode="numeric" autoComplete="off"
                    maxLength={4} value={again}
                    onChange={(e) => { setAgain(digitsOnly(e.target.value)); setPinError(null) }}
                  />
                </div>
                <p className="m-note wide">
                  {t('account.sPinAdvice')}
                </p>
              </>
            )}

            {mode === 'off' && (
              <p className="m-note wide">
                {t('account.sOffNote')}
              </p>
            )}

            <div className="m-actions wide">
              <button type="submit" className={`m-btn grow${mode === 'off' ? ' danger' : ''}`}>
                {mode === 'off' ? t('account.sTurnOff') : mode === 'change' ? t('account.sSaveNewPin') : t('account.sTurnOn')}
              </button>
              <button type="button" className="m-btn light" onClick={() => openMode('idle')}>{t('common.cancel')}</button>
            </div>
          </form>
        )}
      </div>
      </div>
    </Screen>
  )
}
